import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MobilePaymentType } from '@app/sales/enums/mobile-payment-type';
import { VoiceAllowedChangeInfo } from '@app/sales/modules/subscription/models/offerings-response.model';
import { PcmProductCategoryType } from '@app/sales/modules/subscription/models/pcm-product.model';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { getAgeLimitStatus } from '@app/sales/validators/age.validator';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { PriceType } from '@app/shared/pipes/price-format.pipe';
import { PriceFormatPipe } from '../../pipes/price-format.pipe';
import { DatePipe } from '@angular/common';
import { IceBadgeComponent } from '../atoms/ice-badge/ice-badge.component';
import { SpinnerComponent } from '../atoms/spinner/spinner.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioButton } from '@angular/material/radio';

@Component({
  selector: 'app-hardware-voice-product-upgrade',
  templateUrl: './hardware-voice-product-upgrade.component.html',
  styleUrl: './hardware-voice-product-upgrade.component.scss',
  imports: [MatRadioButton, MatTooltip, SpinnerComponent, IceBadgeComponent, DatePipe, PriceFormatPipe],
})
export class HardwareVoiceProductUpgradeComponent implements OnInit, OnChanges {
  @Input({ required: true }) voiceProduct: VoiceProduct;
  @Input({ required: true }) newOfferingId: string;
  @Input({ required: true }) subscriptionToUpgrade: string;
  @Input({ required: false }) paymentType: MobilePaymentType;
  @Input({ required: false }) existingProductsAvailableForEquipmentUpgrade: VoiceProduct[];
  @Output() resetSubscriptionIdToUpgrade = new EventEmitter();

  priceType = PriceType;
  SubscriptionHelperService = SubscriptionHelperService;
  changeInfoForUpgrade: VoiceAllowedChangeInfo;
  isUpgradeAllowed: boolean;
  subscriberExceedsUpperAgeLimit: boolean;
  subscriberIsBelowLowerAgeLimit: boolean;
  tooltipText: string = '';

  get displayTerminationFee(): boolean {
    const { triggersBreakageFee, remainingBreakageFee } = this.changeInfoForUpgrade?.changeInfo || {};
    const expirationDate = this.voiceProduct.subscription?.commitmentPeriods?.[0]?.expirationDate;

    return (
      !!this.isUpgradeAllowed &&
      !this.subscriberExceedsUpperAgeLimit &&
      !this.subscriberIsBelowLowerAgeLimit &&
      triggersBreakageFee &&
      remainingBreakageFee?.includingVAT > 0 &&
      new Date(expirationDate)?.getTime() > new Date().getTime()
    );
  }

  get vasOfferingProductName(): string {
    const vasAddon = this.voiceProduct.assignedSubscription?.addons.find(
      addon => addon.productDetails.pcmProductCategory?.value === PcmProductCategoryType.ValueAddedService
    );
    return !!vasAddon ? `(${vasAddon.productDetails.name})` : '';
  }

  ngOnInit() {
    this.changeInfoForUpgrade = this.voiceProduct.allowedSubscriptionOfferings?.find(
      x => x.subscriptionOffering.id === this.newOfferingId
    );
    this.isUpgradeAllowed =
      !!this.changeInfoForUpgrade &&
      (this.paymentType !== MobilePaymentType.EquipmentUpgrade ||
        this.existingProductsAvailableForEquipmentUpgrade?.some(x => x.id === this.voiceProduct.id));
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.voiceProduct || changes.newOfferingId) {
      this.changeInfoForUpgrade = this.voiceProduct.allowedSubscriptionOfferings?.find(
        x => x.subscriptionOffering.id === this.newOfferingId
      );

      const { upperAgeLimit, lowerAgeLimit, name } = this.changeInfoForUpgrade?.subscriptionOffering || {};
      const { exceedsUpperAgeLimit, isBelowLowerAgeLimit } = getAgeLimitStatus(
        this.voiceProduct?.subscriber?.birthday,
        this.changeInfoForUpgrade?.subscriptionOffering
      );
      this.subscriberExceedsUpperAgeLimit = exceedsUpperAgeLimit;
      this.subscriberIsBelowLowerAgeLimit = isBelowLowerAgeLimit;

      this.isUpgradeAllowed =
        !!this.changeInfoForUpgrade &&
        (this.paymentType !== MobilePaymentType.EquipmentUpgrade ||
          this.existingProductsAvailableForEquipmentUpgrade?.some(x => x.id === this.voiceProduct.id));

      if (this.subscriberExceedsUpperAgeLimit) {
        this.tooltipText = `Bruker er over ${upperAgeLimit} år og kan ikke kjøpe ${name}, endre bruker først for å velge abonnement.`;
      } else if (this.subscriberIsBelowLowerAgeLimit) {
        this.tooltipText = `Bruker er under ${lowerAgeLimit} år og kan ikke kjøpe ${name}, endre bruker først for å velge abonnement.`;
      } else if (!this.isUpgradeAllowed) {
        this.tooltipText = 'Abonnement ikke tilgjengelig';
      } else {
        this.tooltipText = '';
      }

      const isVoiceSubscriptionNotAllowedToSelect =
        !this.isUpgradeAllowed || this.subscriberExceedsUpperAgeLimit || this.subscriberIsBelowLowerAgeLimit;
      const isVoiceSubscriptionAlreadySelected = this.voiceProduct.id === this.subscriptionToUpgrade;

      if (isVoiceSubscriptionNotAllowedToSelect && isVoiceSubscriptionAlreadySelected) {
        this.resetSubscriptionIdToUpgrade.emit();
      }
    }
  }
}
